export class Extent {
  constructor(
    public minX: number,
    public minY: number,
    public maxX: number,
    public maxY: number
  ) {}

  getWidth(): number {
    return this.maxX - this.minX;
  }

  getHeight(): number {
    return this.maxY - this.minY;
  }

  getCenter(): [number, number] {
    return [(this.minX + this.maxX) / 2, (this.minY + this.maxY) / 2];
  }

  getTopLeft(): [number, number] {
    return [this.minX, this.maxY];
  }

  getTopRight(): [number, number] {
    return [this.maxX, this.maxY];
  }

  getBottomLeft(): [number, number] {
    return [this.minX, this.minY];
  }

  getBottomRight(): [number, number] {
    return [this.maxX, this.minY];
  }

  contains(coord: [number, number]): boolean {
    return (
      coord[0] >= this.minX &&
      coord[0] <= this.maxX &&
      coord[1] >= this.minY &&
      coord[1] <= this.maxY
    );
  }

  containsExtent(other: Extent): boolean {
    return (
      other.minX >= this.minX &&
      other.maxX <= this.maxX &&
      other.minY >= this.minY &&
      other.maxY <= this.maxY
    );
  }

  intersects(other: Extent): boolean {
    return (
      this.minX <= other.maxX &&
      this.maxX >= other.minX &&
      this.minY <= other.maxY &&
      this.maxY >= other.minY
    );
  }

  extend(coord: [number, number]): Extent {
    return new Extent(
      Math.min(this.minX, coord[0]),
      Math.min(this.minY, coord[1]),
      Math.max(this.maxX, coord[0]),
      Math.max(this.maxY, coord[1])
    );
  }

  buffer(distance: number): Extent {
    return new Extent(
      this.minX - distance,
      this.minY - distance,
      this.maxX + distance,
      this.maxY + distance
    );
  }

  getIntersection(other: Extent): Extent | null {
    if (!this.intersects(other)) return null;
    return new Extent(
      Math.max(this.minX, other.minX),
      Math.max(this.minY, other.minY),
      Math.min(this.maxX, other.maxX),
      Math.min(this.maxY, other.maxY)
    );
  }

  isEmpty(): boolean {
    return this.getWidth() <= 0 || this.getHeight() <= 0;
  }

  clone(): Extent {
    return new Extent(this.minX, this.minY, this.maxX, this.maxY);
  }

  toArray(): [number, number, number, number] {
    return [this.minX, this.minY, this.maxX, this.maxY];
  }

  toPolygonCoordinates(): [number, number][][] {
    return [
      [
        [this.minX, this.minY],
        [this.maxX, this.minY],
        [this.maxX, this.maxY],
        [this.minX, this.maxY],
        [this.minX, this.minY],
      ],
    ];
  }
}
